/**
 * Разметка статусов и дельты для строк хранилища.
 *
 * Формат денег, дат и множественных чисел берём из отчёта: список и документ
 * должны говорить одними и теми же словами, иначе «88 400 ₽» в строке и
 * «88 400 ₽» на этаже рисков читаются как два разных числа.
 *
 * @format
 */

import type {PaletteTone} from '../ExecutiveReport/content';
import type {ReportStatus} from './types';

/** Формы для счётчика периодов в заголовке списка — как `TASKS` в отчёте. */
export const PERIODS: [string, string, string] = ['период', 'периода', 'периодов'];

/**
 * Доля риска в расходе, целые проценты.
 *
 * Возвращает число, а не готовую подпись: собирать из него строку — дело
 * компонента, как и везде в отчёте. `null`, когда считать не из чего —
 * расход неизвестен или нулевой (деление на ноль дало бы «Infinity %»).
 */
export function riskSharePct(atRisk: number, spend?: number): number | null {
  if (spend == null || spend <= 0) return null;
  return Math.round((atRisk / spend) * 100);
}

export interface StatusMeta {
  label: string;
  icon: string;
  /**
   * Тон бейджа. `neutral` — для запланированного: это не оценка, а факт.
   *
   * Это ключ палитры (`good`/`alert`/`bad`), а не код тяжести из схемы
   * (`GOOD`/`ALERT`/`BAD`): статус хранилища сервер не оценивает, тон мы
   * выбираем сами и отдаём прямо в проп компонента.
   */
  tone: PaletteTone | 'neutral';
  /** Подпись даты рядом со статусом: у каждого статуса своя. */
  dateLabel: string;
}

const STATUS_META: Record<ReportStatus, StatusMeta> = {
  ready: {label: 'Готов', icon: 'check_circle', tone: 'good', dateLabel: 'Собран'},
  building: {label: 'Собирается', icon: 'progress_activity', tone: 'alert', dateLabel: 'Начат'},
  scheduled: {label: 'Запланирован', icon: 'schedule', tone: 'neutral', dateLabel: 'Соберётся'},
  failed: {label: 'Не собран', icon: 'error', tone: 'bad', dateLabel: 'Прервано'},
};

export function statusMeta(status: ReportStatus): StatusMeta {
  return STATUS_META[status];
}

/**
 * Тон дельты балла.
 *
 * Рост — хорошо, падение — плохо, ноль — нейтрально. Порога здесь нет
 * намеренно: балл целочисленный, и «+1» на шкале из ста уже событие, если
 * до этого три месяца стояло ровно.
 */
export function deltaTone(delta: number): PaletteTone | 'neutral' {
  if (delta > 0) return 'good';
  if (delta < 0) return 'bad';
  return 'neutral';
}

/**
 * Тон балла по каноничным полосам отчёта (`score.ranges` в `mocks.ts`):
 * до 50 — плохо, 50–65 — требует внимания, дальше норма. Держим границы
 * теми же, иначе строка списка и шкала внутри отчёта разойдутся в цвете на
 * одном и том же балле.
 */
export function scoreTone(score: number): PaletteTone {
  if (score >= 65) return 'good';
  if (score >= 50) return 'alert';
  return 'bad';
}
